import React, { useEffect, useState } from "react";
import { useI18n } from "../lib/i18n";
import api from "../lib/api";
import { useTenant } from "../lib/tenant";
import { Building2, Plus, Save, Users, Palette, X, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import LogoUploader from "../components/LogoUploader";

const DEFAULT_BRANDING = {
  primary: "#475569", secondary: "#94a3b8", accent: "#dac28a",
  surface: "#f6f6f6", text: "#323232",
  success: "#34c759", warning: "#f59e0b", danger: "#881a27",
  font_family: "Manrope",
  logo_url: "", logo_mark_url: "",
};

const FONT_CHOICES = ["Manrope", "Inter", "Roboto", "Poppins", "Montserrat", "Open Sans", "DM Sans", "Lato", "Space Grotesk"];

export default function SuperAdmin() {
  const { t } = useI18n();
  const [tenants, setTenants] = useState([]);
  const [editing, setEditing] = useState(null);   // tenant id currently being edited
  const [deleting, setDeleting] = useState(null); // tenant object being deleted (with confirmation modal)
  const [creating, setCreating] = useState(false);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/superadmin/tenants");
      setTenants(data);
    } catch (e) { toast.error(t("sa.list_fail")); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold text-[#1e293b] tracking-tight">Super Admin · Empresas</h1>
          <p className="text-[13px] text-[#64748b] mt-1">{t("sa.subtitle")}</p>
        </div>
        <button data-testid="new-tenant-btn" onClick={() => setCreating(true)} className="sw-btn-primary">
          <Plus size={16} /> Nova empresa
        </button>
      </div>

      {creating && <NewTenantForm onClose={() => setCreating(false)} onCreated={() => { setCreating(false); load(); }} />}

      <div className="sw-card !p-0 overflow-hidden">
        <table className="w-full text-[13.5px]">
          <thead className="bg-[#f6f6f6] border-b border-[#eeeded] text-[#475569] text-[11px] uppercase tracking-wider">
            <tr>
              <th className="text-left px-5 py-3">{t("common.company")}</th>
              <th className="text-left px-5 py-3">Slug</th>
              <th className="text-left px-5 py-3">Tagline</th>
              <th className="text-left px-5 py-3">Branding</th>
              <th className="text-left px-5 py-3">{t("sa.users_col")}</th>
              <th className="text-left px-5 py-3">{t("common.status")}</th>
              <th className="text-right px-5 py-3">{t("common.actions")}</th>
            </tr>
          </thead>
          <tbody>
            {tenants.map((t) => (
              <tr key={t.id} data-testid={`tenant-row-${t.id}`} className="border-t border-[#eeeded] hover:bg-[#fafafa]">
                <td className="px-5 py-3 font-medium text-[#1e293b]">{t.name}</td>
                <td className="px-5 py-3 text-[#64748b] font-mono text-[12px]">{t.id}</td>
                <td className="px-5 py-3 text-[#64748b]">{t.tagline || "—"}</td>
                <td className="px-5 py-3">
                  <div className="flex gap-1">
                    {["primary","secondary","accent","success","warning","danger"].map((k) => (
                      <span key={k} className="w-5 h-5 rounded border border-[#e2e8f0]" style={{ background: t.branding?.[k] || "#fff" }} title={k} />
                    ))}
                  </div>
                </td>
                <td className="px-5 py-3">{t.user_count ?? 0}</td>
                <td className="px-5 py-3">
                  <span className={`sw-badge ${t.active ? "sw-badge-approved" : "sw-badge-rejected"}`}>{t.active ? "Ativo" : "Suspenso"}</span>
                </td>
                <td className="px-5 py-3 text-right">
                  <div className="inline-flex gap-1.5">
                    <button data-testid={`edit-tenant-${t.id}`} onClick={() => setEditing(t.id)} className="sw-btn-secondary !py-1.5 !px-3 !text-[12px]">
                      <Palette size={13} /> Editar
                    </button>
                    {t.id !== "sagewood" && (
                      <button data-testid={`delete-tenant-${t.id}`} onClick={() => setDeleting(t)} className="sw-btn-secondary !py-1.5 !px-3 !text-[12px] !text-[#881a27] hover:!bg-[#fef2f2]">
                        <X size={13} /> Excluir
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
            {tenants.length === 0 && !loading && (
              <tr><td colSpan={7} className="px-5 py-12 text-center text-[#94a3b8]">{t("sa.no_companies")}</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {editing && <TenantEditor tid={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}
      {deleting && <TenantDeleteModal tenant={deleting} onClose={() => setDeleting(null)} onDeleted={() => { setDeleting(null); load(); }} />}
    </div>
  );
}

function TenantDeleteModal({ tenant, onClose, onDeleted }) {
  const { t } = useI18n();
  const [confirm, setConfirm] = useState("");
  const [deleting, setDeleting] = useState(false);
  const [users, setUsers] = useState(null);

  useEffect(() => {
    api.get(`/superadmin/tenants/${tenant.id}/users`).then((r) => setUsers(r.data)).catch(() => setUsers([]));
  }, [tenant.id]);

  const submit = async () => {
    if (confirm !== tenant.id) { toast.error(t("sa.type_exact", { slug: tenant.id })); return; }
    setDeleting(true);
    try {
      const { data } = await api.delete(`/superadmin/tenants/${tenant.id}`, { data: { confirm: tenant.id, delete_users: true } });
      toast.success(t("sa.company_deleted", { name: tenant.name }) + ` · ${data.total_docs_deleted} docs`);
      onDeleted();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro ao excluir empresa");
    } finally { setDeleting(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-6">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg" data-testid="delete-tenant-modal">
        <div className="border-b border-[#eeeded] px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-[#fef2f2] flex items-center justify-center">
              <AlertTriangle size={20} className="text-[#881a27]" />
            </div>
            <h3 className="font-semibold text-[#1e293b] text-[16px]">{t("sa.delete_company")}</h3>
          </div>
          <button onClick={onClose} className="text-[#94a3b8] hover:text-[#1e293b]"><X size={18} /></button>
        </div>

        <div className="p-6 space-y-4">
          <div className="bg-[#fef2f2] border border-[#fecaca] rounded-lg p-4 text-[13px] text-[#7f1d1d] leading-relaxed">
            <strong>{t("sa.irreversible")}</strong> Toda a informação da empresa será deletada permanentemente:
            usuários, departamentos, PTO, reembolsos, viagens, empréstimos, performance, hiring, onboarding,
            documentos, knowledge base, tokens QBO, configurações etc.
          </div>

          <div className="text-[13px] text-[#475569]">
            <div><strong>{t("sa.company_label")}</strong> {tenant.name}</div>
            <div><strong>Slug:</strong> <span className="font-mono">{tenant.id}</span></div>
            <div><strong>{t("sa.users_delete")}</strong> {users === null ? "carregando..." : users.length}</div>
            {users && users.length > 0 && (
              <ul className="mt-1 text-[12px] text-[#94a3b8] list-disc list-inside max-h-[100px] overflow-y-auto">
                {users.slice(0, 8).map((u) => <li key={u.id}>{u.email} ({u.role})</li>)}
                {users.length > 8 && <li>... e mais {users.length - 8}</li>}
              </ul>
            )}
          </div>

          <div>
            <label className="sw-label">{t("sa.type_slug")} <span className="font-mono text-[#881a27]">{tenant.id}</span></label>
            <input
              data-testid="delete-tenant-confirm-input"
              className="sw-input font-mono"
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              placeholder={tenant.id}
              autoFocus
            />
          </div>
        </div>

        <div className="border-t border-[#eeeded] px-6 py-4 flex justify-end gap-2">
          <button onClick={onClose} className="sw-btn-secondary">{t("common.cancel")}</button>
          <button
            data-testid="delete-tenant-submit"
            onClick={submit}
            disabled={deleting || confirm !== tenant.id}
            className="sw-btn-primary !bg-[#881a27] hover:!bg-[#7f1d1d] disabled:!opacity-40"
          >
            <X size={14} /> {deleting ? "Excluindo..." : `Excluir ${tenant.name}`}
          </button>
        </div>
      </div>
    </div>
  );
}

function NewTenantForm({ onClose, onCreated }) {
  const { t } = useI18n();
  const [form, setForm] = useState({ id: "", name: "", short_name: "", tagline: "", active: true });
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    if (!form.id || !form.name) { toast.error(t("sa.slug_name_required")); return; }
    setSaving(true);
    try {
      await api.post("/superadmin/tenants", { ...form });
      toast.success(t("sa.company_created"));
      onCreated();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro ao criar empresa");
    } finally { setSaving(false); }
  };

  return (
    <div className="sw-card border-[2px] border-[#475569]">
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-semibold text-[#1e293b] text-[15px]">{t("sa.new_company")}</h3>
        <button onClick={onClose} className="text-[#94a3b8] hover:text-[#1e293b]"><X size={18} /></button>
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="sw-label">Slug (URL friendly)</label>
          <input data-testid="new-tenant-id" className="sw-input" placeholder="acme-corp" value={form.id} onChange={(e) => setForm({ ...form, id: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, "-") })} />
          <p className="text-[11px] text-[#94a3b8] mt-1">{t("sa.slug_hint")}</p>
        </div>
        <div>
          <label className="sw-label">Nome completo</label>
          <input data-testid="new-tenant-name" className="sw-input" placeholder="ACME Corporation" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
        </div>
        <div>
          <label className="sw-label">Nome curto</label>
          <input data-testid="new-tenant-shortname" className="sw-input" placeholder="ACME" value={form.short_name} onChange={(e) => setForm({ ...form, short_name: e.target.value })} />
        </div>
        <div>
          <label className="sw-label">Tagline (texto do hero/landing)</label>
          <input className="sw-input" placeholder="Welcome Home, ACME Team" value={form.tagline} onChange={(e) => setForm({ ...form, tagline: e.target.value })} />
        </div>
      </div>
      <div className="flex justify-end gap-2 mt-5">
        <button onClick={onClose} className="sw-btn-secondary">{t("common.cancel")}</button>
        <button data-testid="submit-new-tenant" onClick={submit} disabled={saving} className="sw-btn-primary">
          <Plus size={14} /> {saving ? "Criando..." : "Criar empresa"}
        </button>
      </div>
    </div>
  );
}

function TenantEditor({ tid, onClose, onSaved }) {
  const { t } = useI18n();
  const [tenant, setTenant] = useState(null);
  const [branding, setBranding] = useState(DEFAULT_BRANDING);
  const [meta, setMeta] = useState({ name: "", short_name: "", tagline: "", anniversary_badge: "", active: true });
  const [saving, setSaving] = useState(false);
  const [seedingAdmin, setSeedingAdmin] = useState(false);
  const [adminForm, setAdminForm] = useState({ email: "", name: "", password: "" });

  useEffect(() => {
    api.get(`/superadmin/tenants/${tid}`).then((r) => {
      setTenant(r.data);
      setBranding({ ...DEFAULT_BRANDING, ...(r.data.branding || {}) });
      setMeta({ name: r.data.name, short_name: r.data.short_name || "", tagline: r.data.tagline || "", anniversary_badge: r.data.anniversary_badge || "", active: r.data.active });
    });
  }, [tid]);

  const save = async () => {
    setSaving(true);
    try {
      await api.patch(`/superadmin/tenants/${tid}`, { ...meta, branding });
      toast.success(t("sa.company_updated"));
      onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || "Erro ao salvar"); }
    finally { setSaving(false); }
  };

  const seedAdmin = async () => {
    if (!adminForm.email || !adminForm.name || !adminForm.password) {
      toast.error(t("sa.fill_admin")); return;
    }
    setSeedingAdmin(true);
    try {
      await api.post(`/superadmin/tenants/${tid}/seed-admin`, { ...adminForm, role: "admin" });
      toast.success(t("sa.admin_created", { email: adminForm.email }));
      setAdminForm({ email: "", name: "", password: "" });
    } catch (e) { toast.error(e?.response?.data?.detail || "Erro ao criar admin"); }
    finally { setSeedingAdmin(false); }
  };

  if (!tenant) return null;

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-6">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-4xl max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b border-[#e2e8f0] px-6 py-4 flex items-center justify-between z-10">
          <div className="flex items-center gap-3">
            <Building2 size={20} className="text-[#475569]" />
            <h3 className="font-semibold text-[#1e293b] text-[16px]">Editar empresa · <span className="font-mono text-[14px] text-[#64748b]">{tid}</span></h3>
          </div>
          <button onClick={onClose} className="text-[#94a3b8] hover:text-[#1e293b]"><X size={18} /></button>
        </div>

        <div className="p-6 space-y-6">
          {/* Meta */}
          <section>
            <h4 className="text-[11px] uppercase tracking-wider text-[#94a3b8] font-semibold mb-3">Identidade</h4>
            <div className="grid grid-cols-2 gap-4">
              <div><label className="sw-label">Nome</label><input className="sw-input" value={meta.name} onChange={(e) => setMeta({ ...meta, name: e.target.value })} /></div>
              <div><label className="sw-label">Nome curto</label><input className="sw-input" value={meta.short_name} onChange={(e) => setMeta({ ...meta, short_name: e.target.value })} /></div>
              <div className="col-span-2"><label className="sw-label">Tagline</label><input className="sw-input" value={meta.tagline} onChange={(e) => setMeta({ ...meta, tagline: e.target.value })} /></div>
              <div className="col-span-2"><label className="sw-label">Selo de aniversário (opcional · ex: &quot;25 anos&quot;)</label><input className="sw-input" placeholder="25 years" value={meta.anniversary_badge} onChange={(e) => setMeta({ ...meta, anniversary_badge: e.target.value })} /></div>
              <div className="col-span-2">
                <label className="inline-flex items-center gap-2 cursor-pointer">
                  <input type="checkbox" checked={meta.active} onChange={(e) => setMeta({ ...meta, active: e.target.checked })} className="w-4 h-4 accent-[#475569]" />
                  <span className="text-[13px] text-[#1e293b]">{t("sa.company_active")}</span>
                </label>
              </div>
            </div>
          </section>

          {/* Branding */}
          <section>
            <h4 className="text-[11px] uppercase tracking-wider text-[#94a3b8] font-semibold mb-3">Branding · paleta de cores</h4>
            <div className="grid grid-cols-3 gap-4">
              <ColorField label={t("brand.primary")} testid="color-primary" value={branding.primary} onChange={(v) => setBranding({ ...branding, primary: v })} />
              <ColorField label={t("brand.secondary")} testid="color-secondary" value={branding.secondary} onChange={(v) => setBranding({ ...branding, secondary: v })} />
              <ColorField label="Detalhe / Acento" testid="color-accent" value={branding.accent} onChange={(v) => setBranding({ ...branding, accent: v })} />
              <ColorField label={t("brand.success")} value={branding.success} onChange={(v) => setBranding({ ...branding, success: v })} />
              <ColorField label="Alerta" value={branding.warning} onChange={(v) => setBranding({ ...branding, warning: v })} />
              <ColorField label="Erro" value={branding.danger} onChange={(v) => setBranding({ ...branding, danger: v })} />
              <ColorField label="Fundo (surface)" value={branding.surface} onChange={(v) => setBranding({ ...branding, surface: v })} />
              <ColorField label="Texto" value={branding.text} onChange={(v) => setBranding({ ...branding, text: v })} />
            </div>
          </section>

          {/* Logo URLs + Font */}
          <section>
            <h4 className="text-[11px] uppercase tracking-wider text-[#94a3b8] font-semibold mb-3">Logo · Fonte</h4>
            <div className="grid grid-cols-2 gap-4">
              <LogoUploader
                label="Logo completo"
                value={branding.logo_url}
                onChange={(v) => setBranding({ ...branding, logo_url: v })}
                testid="sa-logo-full"
                aspect="wide"
              />
              <LogoUploader
                label={t("brand.logo_mark")}
                value={branding.logo_mark_url}
                onChange={(v) => setBranding({ ...branding, logo_mark_url: v })}
                testid="sa-logo-mark"
                aspect="square"
              />
              <div className="col-span-2">
                <label className="sw-label">Fonte personalizada</label>
                <select className="sw-input" value={branding.font_family || "Manrope"} onChange={(e) => setBranding({ ...branding, font_family: e.target.value })}>
                  {FONT_CHOICES.map((f) => <option key={f} value={f}>{f}</option>)}
                </select>
                <p className="text-[11px] text-[#94a3b8] mt-1">{t("sa.font_hint")}</p>
              </div>
            </div>
          </section>

          {/* Live preview */}
          <section>
            <h4 className="text-[11px] uppercase tracking-wider text-[#94a3b8] font-semibold mb-3">Preview</h4>
            <BrandingPreview branding={branding} />
          </section>

          {/* Seed admin */}
          <section className="border-t border-[#e2e8f0] pt-6">
            <h4 className="text-[11px] uppercase tracking-wider text-[#94a3b8] font-semibold mb-3 flex items-center gap-2"><Users size={13} />  {t("sa.create_admin")}</h4>
            <div className="grid grid-cols-3 gap-3">
              <input data-testid="seed-admin-name" className="sw-input" placeholder="Nome do admin" value={adminForm.name} onChange={(e) => setAdminForm({ ...adminForm, name: e.target.value })} />
              <input data-testid="seed-admin-email" className="sw-input" placeholder="admin@empresa.com" value={adminForm.email} onChange={(e) => setAdminForm({ ...adminForm, email: e.target.value })} />
              <input data-testid="seed-admin-pwd" className="sw-input" placeholder={t("hire.initial_pwd_ph")} value={adminForm.password} onChange={(e) => setAdminForm({ ...adminForm, password: e.target.value })} />
            </div>
            <button data-testid="seed-admin-btn" onClick={seedAdmin} disabled={seedingAdmin} className="mt-3 sw-btn-secondary">
              <Plus size={13} /> {seedingAdmin ? "Criando..." : "Criar admin"}
            </button>
          </section>
        </div>

        <div className="sticky bottom-0 bg-white border-t border-[#e2e8f0] px-6 py-4 flex justify-end gap-2">
          <button onClick={onClose} className="sw-btn-secondary">{t("common.cancel")}</button>
          <button data-testid="save-tenant-btn" onClick={save} disabled={saving} className="sw-btn-primary">
            <Save size={14} /> {saving ? "Salvando..." : "Salvar alterações"}
          </button>
        </div>
      </div>
    </div>
  );
}

function ColorField({ label, value, onChange, testid }) {
  return (
    <div>
      <label className="sw-label">{label}</label>
      <div className="flex items-center gap-2">
        <input
          type="color"
          data-testid={testid}
          value={value || "#000000"}
          onChange={(e) => onChange(e.target.value)}
          className="w-12 h-10 rounded border border-[#e2e8f0] cursor-pointer shrink-0"
        />
        <input
          type="text"
          value={value || ""}
          onChange={(e) => onChange(e.target.value)}
          className="sw-input flex-1 font-mono text-[12px]"
          placeholder="#475569"
        />
      </div>
    </div>
  );
}

function BrandingPreview({ branding }) {
  const { t } = useI18n();
  return (
    <div className="rounded-xl border border-[#e2e8f0] overflow-hidden">
      <div className="px-5 py-4 flex items-center justify-between" style={{ background: branding.primary, color: "white" }}>
        <div className="flex items-center gap-3">
          {branding.logo_mark_url && <img src={branding.logo_mark_url} alt="" className="w-9 h-9 object-contain" style={{ filter: "brightness(0) invert(1)" }} />}
          <div>
            <div className="font-semibold tracking-wide">{branding.tenant_name || "Sua empresa"}</div>
            <div className="text-[11px] opacity-80">Hero / Sidebar header</div>
          </div>
        </div>
        <button className="rounded-full px-4 py-1.5 text-[12px] font-medium" style={{ background: branding.accent, color: branding.text }}>{t("sa.action_button")}</button>
      </div>
      <div className="p-5 grid grid-cols-3 gap-3" style={{ background: branding.surface, color: branding.text, fontFamily: `'${branding.font_family || "Manrope"}', sans-serif` }}>
        <PreviewCard color={branding.success} label={t("brand.approved")} branding={branding} />
        <PreviewCard color={branding.warning} label={t("brand.pending")} branding={branding} />
        <PreviewCard color={branding.danger} label={t("brand.rejected")} branding={branding} />
      </div>
    </div>
  );
}
function PreviewCard({ color, label, branding }) {
  return (
    <div className="rounded-lg p-3 bg-white border border-[#e2e8f0] flex items-center gap-2">
      <span className="w-3 h-3 rounded-full" style={{ background: color }} />
      <div className="text-[12px]" style={{ color: branding.text }}>{label}</div>
    </div>
  );
}
